import {
  NoInnerEventsComponent
} from "./chunk.54YXW2TU.js";
import {
  emit
} from "./chunk.HM4ZRC4T.js";
import {
  watch
} from "./chunk.HBM5K2JV.js";
import {
  __decorateClass
} from "./chunk.U4VUMZVF.js";

// src/utilities/scrolled-element.ts
function isElementVerticallyScrolled(element, wrappingElement) {
  const elementRect = element.getBoundingClientRect();
  const wrappingElementRect = wrappingElement.getBoundingClientRect();
  return elementRect.y >= wrappingElementRect.y + wrappingElementRect.height;
}
function isElementHorizontallyScrolled(element) {
  return element.scrollLeft > 0;
}

// src/utilities/selectable-list-component.ts
import { html } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { property, query, state } from "lit/decorators.js";
var YotpoSelectableTriggerTags = [
  "yotpo-button",
  "yotpo-text-button",
  "yotpo-icon-button",
  "yotpo-input",
  "yotpo-dropdown-input"
];
var YotpoSelectableInformativeTriggerTags = ["yotpo-badge"];
var YotpoSelectableComponentsWidthByContainer = ["yotpo-input", "yotpo-dropdown"];
var SelectableListComponent = class extends NoInnerEventsComponent {
  constructor() {
    super(...arguments);
    this.clearWhenHidden = false;
    this.stayOpenOnSelect = false;
    this.multiSelectable = false;
    this.disabled = false;
    this.margins = true;
    this.visibilityEnabled = true;
    this.hoist = true;
    this.trigger = "click";
    this.infiniteScrollable = false;
    this.scrollEndOffset = 0;
  }
  set open(isOpen) {
    if (!this.popover)
      return;
    isOpen ? this.show() : this.hide();
  }
  get open() {
    if (!this.popover)
      return false;
    return this.popover.isShown();
  }
  async firstUpdated() {
    await this.updateComplete;
    this.handleMultiSelectableChange();
    this.handleDisabledChange();
    this.handlePopoverInitialDisplay();
    if (this.infiniteScrollable) {
      this.onInfiniteScrollableUpdate();
      this.onScrollEndOffsetUpdate();
    }
  }
  async show() {
    this.popover.show();
  }
  async hide() {
    this.popover.hide();
  }
  scrollToTop() {
    if (this.infiniteScrollable && this.popover) {
      this.popover.scrollToTop();
    }
  }
  getAccessibleTrigger() {
    const target = this.popover.getTarget();
    if (!target)
      return;
    const slot = target.querySelector("slot[name=target]");
    if (slot) {
      const assignedElements = slot.assignedElements({ flatten: true });
      return assignedElements[0];
    }
    return target.querySelector('[part="target"]');
  }
  focusOnTrigger() {
    const trigger = this.getAccessibleTrigger();
    if (typeof (trigger == null ? void 0 : trigger.focus) === "function")
      trigger.focus();
  }
  getList() {
    return this.querySelector("yotpo-list");
  }
  handlePopoverHiding(event) {
    var _a;
    if (this.clearWhenHidden)
      (_a = this.getList()) == null ? void 0 : _a.clearSelection();
  }
  handlePopoverShowing() {
    const list = this.getList();
    if (!list) {
      return;
    }
    const currentListItem = list.getCurrentItem();
    if (currentListItem && isElementVerticallyScrolled(currentListItem, this.popover.popover)) {
      currentListItem.scrollIntoView();
    }
  }
  handlePopoverSelected(event) {
  }
  handlePopoverSelectionChanged(event) {
    const target = event.target;
    const keepOpenAnyway = this.stayOpenOnSelect || this.multiSelectable;
    if (!keepOpenAnyway && target.tagName.toLowerCase() === "yotpo-list") {
      this.popover.hide();
      this.focusOnTrigger();
    }
  }
  handlePopoverScrollEnd(event) {
    event.preventDefault();
    event.stopPropagation();
    emit(this, "yotpo-scroll-end" /* scrollEnd */);
  }
  handleTriggerKeyDown(event) {
    if (event.key === "Escape") {
      this.focusOnTrigger();
      this.popover.hide();
      return;
    }
    if ([" ", "Enter"].includes(event.key)) {
      event.preventDefault();
      return;
    }
    this.handleListKeyboardAction(event);
  }
  handleListKeyboardAction(event) {
    const list = this.getList();
    if (!list)
      return;
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key))
      return this.handleListKeyboardNavigation(list, event);
    if (list.selectionByTyping)
      this.handleListKeyboardSelection(list, event);
  }
  handleListKeyboardNavigation(list, event) {
    event.preventDefault();
    const listItems = list.getAllItems();
    const firstListItem = listItems[0];
    const lastListItem = listItems[listItems.length - 1];
    if (!this.popover.isShown()) {
      this.popover.show();
    }
    if (listItems.length > 0) {
      requestAnimationFrame(() => {
        if (event.key === "ArrowDown" || event.key === "Home") {
          list.setCurrentItem(firstListItem);
          firstListItem.focus();
        }
        if (event.key === "ArrowUp" || event.key === "End") {
          list.setCurrentItem(lastListItem);
          lastListItem.focus();
        }
      });
    }
  }
  handleListKeyboardSelection(list, event) {
    const ignoredKeys = ["Tab", "Shift", "Meta", "Ctrl", "Alt"];
    if (this.popover.isShown() && !ignoredKeys.includes(event.key)) {
      list.typeToSelect(event);
    }
  }
  handleTriggerKeyUp(event) {
    if (event.key === " ") {
      event.preventDefault();
    }
  }
  handleTriggerSlotChange() {
    const accessibleTrigger = this.getAccessibleTrigger();
    if (!accessibleTrigger)
      return;
    const target = this.getTargetElementOfTrigger(accessibleTrigger);
    target.setAttribute("aria-haspopup", "true");
    target.setAttribute("aria-expanded", this.popover.isShown() ? "true" : "false");
  }
  getTargetElementOfTrigger(trigger) {
    switch (trigger.tagName.toLowerCase()) {
      case "yotpo-button":
      case "yotpo-icon-button":
      case "yotpo-text-button":
        return trigger.button;
      case "yotpo-input":
        return trigger.input;
      default:
        return trigger;
    }
  }
  handleMultiSelectableChange() {
    const list = this.getList();
    if (list)
      list.multiSelectable = this.multiSelectable;
  }
  onInfiniteScrollableUpdate() {
    if (this.popover) {
      this.popover.infiniteScrollable = this.infiniteScrollable;
    }
  }
  onScrollEndOffsetUpdate() {
    if (this.popover) {
      this.popover.scrollEndOffset = this.scrollEndOffset;
    }
  }
  onMultiselectableUpdate() {
    this.handleMultiSelectableChange();
  }
  handleDisabledChange() {
    if (this.popover && this.popover.isShown())
      this.popover.hide();
    if (!this.popoverTrigger)
      return;
    const accessibleTrigger = this.getAccessibleTrigger();
    if (!accessibleTrigger)
      return;
    if (YotpoSelectableTriggerTags.includes(accessibleTrigger.tagName.toLowerCase())) {
      accessibleTrigger.disabled = this.disabled;
      return;
    }
    if (YotpoSelectableInformativeTriggerTags.includes(accessibleTrigger.tagName.toLowerCase())) {
      accessibleTrigger.clickable = !this.disabled;
    }
  }
  async handlePopoverInitialDisplay() {
    await this.popover.updateComplete;
    if (!YotpoSelectableComponentsWidthByContainer.includes(this.tagName.toLowerCase())) {
      this.setPopoverWidth("fit-content");
    }
    if (this.open)
      this.popover.show();
  }
  async setPopoverWidth(width) {
    this.popover.style.width = width;
  }
  handleListSlotChange(event) {
    event.stopPropagation();
  }
  renderSelectableList(triggerElement) {
    return html`
			<div
				part="base"
				id="selectlist"
				role="selectlist"
				class=${classMap({
      "select-list--open": this.popover && this.popover.isShown()
    })}
			>
				<yotpo-popover
					part="popover"
					class="select-list__popover"
					.margins=${this.margins}
					.hoist=${this.hoist}
					.visibilityEnabled=${this.visibilityEnabled}
					trigger=${this.trigger}
					@yotpo-show=${this.handlePopoverShowing}
					@yotpo-hide=${this.handlePopoverHiding}
					@yotpo-change=${this.handlePopoverSelectionChanged}
					@yotpo-select=${this.handlePopoverSelected}
					@yotpo-scroll-end=${this.handlePopoverScrollEnd}
				>
					<div
						class="select-list__trigger"
						@keydown=${this.handleTriggerKeyDown}
						@keyup=${this.handleTriggerKeyUp}
					>
						${triggerElement ? triggerElement : html` <slot name="target" @slotchange=${this.handleTriggerSlotChange}></slot>`}
					</div>
					<slot name="header" slot="header"></slot>
					<slot slot="body" @slotchange=${this.handleListSlotChange}></slot>
					<slot name="footer" slot="footer"></slot>
				</yotpo-popover>
			</div>
		`;
  }
};
__decorateClass([
  query(".select-list__trigger")
], SelectableListComponent.prototype, "popoverTrigger", 2);
__decorateClass([
  query(".select-list__popover")
], SelectableListComponent.prototype, "popover", 2);
__decorateClass([
  state()
], SelectableListComponent.prototype, "clearWhenHidden", 2);
__decorateClass([
  state()
], SelectableListComponent.prototype, "stayOpenOnSelect", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], SelectableListComponent.prototype, "multiSelectable", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], SelectableListComponent.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], SelectableListComponent.prototype, "margins", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], SelectableListComponent.prototype, "visibilityEnabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: false })
], SelectableListComponent.prototype, "hoist", 2);
__decorateClass([
  property({ reflect: true })
], SelectableListComponent.prototype, "trigger", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], SelectableListComponent.prototype, "infiniteScrollable", 2);
__decorateClass([
  property({ type: Number, reflect: true })
], SelectableListComponent.prototype, "scrollEndOffset", 2);
__decorateClass([
  watch("infiniteScrollable", { waitUntilFirstUpdate: true })
], SelectableListComponent.prototype, "onInfiniteScrollableUpdate", 1);
__decorateClass([
  watch("scrollEndOffset", { waitUntilFirstUpdate: true })
], SelectableListComponent.prototype, "onScrollEndOffsetUpdate", 1);
__decorateClass([
  watch("multiSelectable", { waitUntilFirstUpdate: true })
], SelectableListComponent.prototype, "onMultiselectableUpdate", 1);
__decorateClass([
  watch("disabled", { waitUntilFirstUpdate: true })
], SelectableListComponent.prototype, "handleDisabledChange", 1);

export {
  isElementHorizontallyScrolled,
  SelectableListComponent
};
